// frontend/src/pages/Admin/TicketSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Spin, Card, Typography, Space, Modal, InputNumber } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const { Title } = Typography;
const { Option } = Select;

const TicketSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [emailGroups, setEmailGroups] = useState([]);

  const notificationApi = useNotificationApi();

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchTicketSettings = useCallback(async () => {
    setInitialDataLoading(true);
    const loadingKey = 'fetchTicketSettingsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading ticket settings...');

    try {
      const response = await api.get('/api/ticket-settings');
      const settings = response.data.data;

      if (settings === null || settings.id === null) {
        notificationApi.info({
          message: 'Ticket settings not initialized.',
          description: 'Displaying default values.',
          placement: 'topRight',
          duration: 3
        });
        form.setFieldsValue({
          ticketUuidPrefix: 'GEN',
          ticketUuidSuffix: '',
          ticketUuidUniquePartLength: 5,
          defaultNewTicketNotificationGroupId: null,
        });
      } else {
        form.setFieldsValue(settings);
        showSuccess('Settings Loaded', 'Ticket settings loaded successfully!', 'fetchSettingsSuccess');
      }
    } catch (error) {
      console.error('Failed to fetch ticket settings:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred while fetching settings.';
      showError('Failed to Fetch Settings', errorMessage, 'fetchSettingsError');
    } finally {
      if (hideLoading) hideLoading();
      setInitialDataLoading(false);
    }
  }, [form, notificationApi, showLoading, showSuccess, showError]);

  const fetchEmailGroups = useCallback(async () => {
    const loadingKey = 'fetchEmailGroupsLoading';
    const hideLoading = showLoading(loadingKey, 'Loading email groups...');
    try {
      const response = await api.get('/api/email-groups?status=active');
      setEmailGroups(response.data.data);
    } catch (error) {
      console.error('Failed to fetch email groups:', error);
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred while fetching email groups.';
      showError('Failed to Load Email Groups', errorMessage, 'fetchEmailGroupsError');
    } finally {
      if (hideLoading) hideLoading();
    }
  }, [showLoading, showError]);

  useEffect(() => {
    fetchTicketSettings();
    fetchEmailGroups();
  }, [fetchTicketSettings, fetchEmailGroups]);

  const onFinish = async (values) => {
    setLoading(true);
    const loadingKey = 'updateTicketSettingsLoading';
    const hideLoading = showLoading(loadingKey, 'Saving ticket settings...');

    try {
      // Validate unique part length before sending
      if (!values.ticketUuidUniquePartLength || 
          values.ticketUuidUniquePartLength < 1 || 
          values.ticketUuidUniquePartLength > 10) {
        throw new Error('Unique part length must be between 1 and 10');
      }

      // Ensure prefix is provided
      if (!values.ticketUuidPrefix) {
        throw new Error('Ticket UUID prefix is required');
      }

      const payload = {
        ticketUuidPrefix: values.ticketUuidPrefix || '',
        ticketUuidSuffix: values.ticketUuidSuffix || '',
        ticketUuidUniquePartLength: values.ticketUuidUniquePartLength,
        defaultNewTicketNotificationGroupId: values.defaultNewTicketNotificationGroupId || null
      };

      await api.put('/api/ticket-settings', payload);
      showSuccess('Settings Updated', 'Ticket settings updated successfully!', 'updateSettingsSuccess');
      fetchTicketSettings(); 
    } catch (error) {
      console.error('Failed to update ticket settings:', error);
      const errorMessage = error.response?.data?.message || error.message;
      showError('Failed to Update Settings', errorMessage, 'updateSettingsError');
    } finally {
      if (hideLoading) hideLoading();
      setLoading(false);
    }
  };

  if (initialDataLoading) {
    return (
      <div className="ticket-settings-page">
        <Spin size="large" tip="Loading settings..." style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }} />
      </div>
    );
  }

  return (
    <div className="ticket-settings-page">
      <Card
        title={<Title level={4}>Global Ticket Settings</Title>}
        bordered={false}
      >
        <div>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="create-ticket-form"
          >
            <Form.Item
              name="ticketUuidPrefix"
              label="Ticket UUID Prefix"
              rules={[
                { required: true, message: 'Please input the ticket prefix!' },
                { max: 50, message: 'Prefix must be 50 characters or less' }
              ]}
            >
              <Input placeholder="e.g., GEN, TK" size="large" className="custom-input" />
            </Form.Item>

            <Form.Item
              name="ticketUuidSuffix"
              label="Ticket UUID Suffix"
              rules={[
                { max: 50, message: 'Suffix must be 50 characters or less' }
              ]}
            >
              <Input placeholder="e.g., PROD (optional)" size="large" className="custom-input" />
            </Form.Item>

            <Form.Item
                name="ticketUuidUniquePartLength"
                label="Unique Part Length (1-10)"
                rules={[
                    { required: true, message: 'Please input the unique part length!' },
                    { 
                    type: 'number',
                    min: 1,
                    max: 10,
                    message: 'Must be between 1 and 10',
                    transform: value => Number(value) // Add this transform function
                    }
            ]}
            >
                <InputNumber 
                    min={1}
                    max={10}
                    placeholder="e.g., 5"
                    size="large"
                    className="custom-input"
                />
            </Form.Item>

            <Form.Item
              name="defaultNewTicketNotificationGroupId"
              label="Default New Ticket Notification Group"
            >
              <Select
                placeholder="Select a group (optional)"
                allowClear
                className="ant-select-lg custom-select custom-select-height custom-select-border-radius"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children && typeof option.children === 'string' && option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {Array.isArray(emailGroups) && emailGroups.map((group) => (
                  <Option key={group.id} value={group.id}>
                    {group.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item className="submit-button-item">
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  className="create-ticket-submit-button"
                >
                  Save Settings
                </Button>
                <Button
                  onClick={fetchTicketSettings}
                  icon={<ReloadOutlined />}
                  className="create-ticket-submit-button"
                >
                  Refresh
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Card>
    </div>
  );
};

export default TicketSettings;